import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import CodeBlock from '@atlaskit/code/block';
import CopyIcon from '@atlaskit/icon/core/copy';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

const wrapperStyles = xcss({ width: '100%', position: 'relative' });
const copyButtonStyles = xcss({
	position: 'absolute',
	top: 'space.0',
	right: 'space.0',
	padding: 'space.050',
	zIndex: 'flag',
});

export type CodeBlockWithCopyButtonProps = {
	lines: string[];
	analyticsSource: string;
};
const i18n = defineMessages({
	copyTooltip: {
		id: 'error-boundary.code-block.copy.tooltip',
		defaultMessage: 'Copy',
		description:
			'Tooltip message for the copy button. Informs a user that they can copy the content to the clipboard.',
	},
	copySuccess: {
		id: 'error-boundary.code-block.copy.success',
		defaultMessage: 'Copied!',
		description:
			'Success message displayed in the tooltip after the user has copied the content to the clipboard.',
	},
	copyFailure: {
		id: 'error-boundary.code-block.copy.failure',
		defaultMessage: 'Copy to clipboard failed',
		description:
			'Failure message displayed in the tooltip of the copy button when the user has clicked the button but something went wrong copying the content to the clipboard.',
	},
});

// This component extends the atlaskit codeblock component with a copy button.
// It's used to display the hash and traceId for the error components and also allows the user to copy the content to the clipboard.
export const CodeBlockWithCopyButton = ({
	lines,
	analyticsSource,
}: CodeBlockWithCopyButtonProps) => {
	const intl = useIntl();
	const [tooltipMessage, setTooltipMessage] = useState<string>(
		intl.formatMessage(i18n.copyTooltip),
	);
	const [justCopied, setJustCopied] = useState<boolean>(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isValidLines = Array.isArray(lines) && lines.every((line) => typeof line === 'string');
	const source = isValidLines ? lines.join('\n') : '';

	const writeToClipboard = async () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'codeBlockCopyToClipboard',
				source: analyticsSource,
			},
		}).fire();

		try {
			await navigator.clipboard.writeText(source);
			setTooltipMessage(intl.formatMessage(i18n.copySuccess));
			setJustCopied(true);
		} catch (error) {
			setTooltipMessage(intl.formatMessage(i18n.copyFailure));
		}
	};
	const resetTooltipMessage = () => {
		if (!justCopied) setTooltipMessage(intl.formatMessage(i18n.copyTooltip));
	};

	const handleBlur = () => {
		setJustCopied(false);
		resetTooltipMessage();
	};

	const handleMouseOver = () => {
		if (!justCopied) resetTooltipMessage();
	};

	const handleMouseLeave = () => {
		setJustCopied(false);
		resetTooltipMessage();
	};

	return (
		<Box xcss={wrapperStyles} role="region">
			<Box xcss={copyButtonStyles}>
				<IconButton
					isTooltipDisabled={false}
					label={tooltipMessage}
					icon={CopyIcon}
					onClick={writeToClipboard}
					onMouseOver={handleMouseOver}
					onMouseLeave={handleMouseLeave}
					onFocus={resetTooltipMessage}
					onBlur={handleBlur}
					aria-live="assertive"
					aria-atomic="true"
					appearance="subtle"
					spacing="compact"
				/>
			</Box>
			<Box>
				<CodeBlock text={source} showLineNumbers={false} shouldWrapLongLines={false} />
			</Box>
		</Box>
	);
};
